import { create } from 'zustand';
import { createJSONStorage, persist } from 'zustand/middleware';

/**
 * vendorAuthStore: 로그인한 사용자의 인증 정보를 저장하는 스토어
 * - 로그인한 소매업자(reseller) 또는 도매업자(supplier)의 인증 정보
 * - accessToken, vendorId, vendorInfo 등
 *
 * 주의: 이 스토어는 '쇼핑몰 주인'이 아닌 '로그인한 사용자'의 정보입니다.
 */

export interface VendorAuthInfo {
    vendorId: string;
    email: string;
    nickname: string;
    storeName?: string | null;
    vendorPath: string;
    profileImage?: string;
    roleName?: string;
    contactInfo?: {
        managerName?: string;
        managerPhoneNumber?: string;
        customerCenterNumber?: string;
    };
    businessInfo?: {
        operatorType?: string;
        businessRegistrationNumber?: string;
        businessType?: string;
    };
    [key: string]: any;
}

export interface VendorAuthStoreState {
    /** JWT 액세스 토큰 */
    accessToken: string;
    /** 토큰 만료 시간 */
    expiresIn: string;
    /** 로그인한 사용자의 vendor ID */
    vendorId: string;
    /** 로그인한 사용자의 상세 정보 */
    vendorInfo: VendorAuthInfo | null;
    /** 로그인 시점의 vendorPath (컨텍스트 검증용) */
    shopVendorPath: string;
}

export interface VendorAuthStoreAction {
    /** 스토어 초기화 (로그아웃 시 사용) */
    reset: () => void;
    /** 인증 정보 설정 (로그인 성공 시 사용) */
    setAuth: (auth: Partial<VendorAuthStoreState>) => void;
    /** 액세스 토큰 설정 */
    setAccessToken: (token: string) => void;
    /** 사용자 정보 설정 */
    setVendorInfo: (info: VendorAuthInfo | null) => void;
    /** 로그인 여부 확인 */
    isAuthenticated: () => boolean;
}

const defaultState: VendorAuthStoreState = {
    accessToken: '',
    expiresIn: '',
    vendorId: '',
    vendorInfo: null,
    shopVendorPath: '',
};

export const useVendorAuthStore = create<VendorAuthStoreState & VendorAuthStoreAction>()(
    persist(
        (set, get) => ({
            ...defaultState,
            reset: () => {
                set(() => ({ ...defaultState }));
            },
            setAuth: (auth) => {
                set((state) => ({
                    ...state,
                    ...auth,
                }));
            },
            setAccessToken: (token) => {
                set(() => ({ accessToken: token }));
            },
            setVendorInfo: (info) => {
                set(() => ({ vendorInfo: info }));
            },
            isAuthenticated: () => {
                const { accessToken, vendorId } = get();
                return !!(accessToken && vendorId);
            },
        }),
        {
            name: 'vendor-auth-storage',
            storage: createJSONStorage(() => localStorage),
        },
    ),
);

// 비-React 환경에서 사용할 수 있는 스토어 인스턴스
export const vendorAuthStore = useVendorAuthStore;
